// Eskom PARCC — login gate.
//
// Full-screen sign-in for the command centre. Email + password only: the admin
// provisions accounts, so there is no sign-up, no backend-URL field and no
// password-reset-email flow in v1. On success the gate disappears, driven by
// auth state (onAuthStateChanged), so there is nothing to dismiss manually.
//
// Visuals reuse the brand login styling lifted from the monolith
// (#loginGate / .login-shell / .login-hero / .login-panel / #loginEmail /
// #enterAppBtn / #loginError) so this looks identical to the original gate.
import { useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../../lib/auth';
import { useVariant } from '../../lib/uiPrefs';

/**
 * The brand wordmark on the login hero, theme-aware.
 *
 * Legacy sits the logo directly on the always-dark hero, so the WHITE wordmark
 * is right. Lumen (per the mockup) puts it on a white card — where the white
 * wordmark vanishes into the card, so it uses the full-colour DARK wordmark.
 * The hero panel is dark in both of Lumen's light/dark sub-modes, so the choice
 * keys off the variant only.
 */
function LoginLogo(): React.JSX.Element {
  const variant = useVariant();
  const src = variant === 'lumen' ? '/logos/eskom-parcc-blk.svg' : '/logos/eskom-parcc-wht.svg';
  return <img className="login-logo" src={src} alt="Eskom PARCC" />;
}

function LoginScreen(): React.JSX.Element {
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      // No navigation here — onAuthStateChanged unmounts this gate.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await signInWithGoogle();
      // No navigation here — onAuthStateChanged unmounts this gate.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div id="loginGate" data-theme="dark">
      <div className="login-shell">
        <section className="login-hero">
          <LoginLogo />
          <h1>Eskom Procurement Audit Recovery Command Centre</h1>
          <p>
            Compliance tracking and audit recovery for Eskom procurement transactions. Sign in to
            continue.
          </p>
          <div className="login-pillrow">
            <span className="login-pill">Eskom PARCC</span>
            <span className="login-pill">Audit recovery</span>
            <span className="login-pill">Secure access</span>
          </div>
        </section>

        <section className="login-panel">
          <div className="eyebrow">Eskom PARCC</div>
          <h2>Sign in</h2>
          <p>Use the email address and password issued to you by your administrator.</p>

          <form onSubmit={handleSubmit} noValidate>
            <label className="login-field" htmlFor="loginEmail">
              Email address
              <input
                id="loginEmail"
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                required
              />
            </label>

            <label className="login-field" htmlFor="loginPassword" style={{ marginTop: 14 }}>
              Password
              <input
                id="loginPassword"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                required
              />
            </label>

            {error && (
              <div id="loginError" role="alert" style={{ display: 'block' }}>
                {error}
              </div>
            )}

            <button id="enterAppBtn" type="submit" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="login-divider" role="separator">
            <span>or</span>
          </div>

          <button
            type="button"
            className="btn login-google"
            onClick={() => void handleGoogle()}
            disabled={submitting}
          >
            <GoogleMark />
            Sign in with Google
          </button>

          <div className="login-note">
            Access is provisioned by your administrator. If you cannot sign in, please contact them
            to confirm your account.
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * NoAccessScreen — shown when a user authenticates successfully but has no
 * access (denied verdict): an unapproved-domain Google account with no grant, or
 * an explicitly revoked account. They are signed in to Firebase but not admitted
 * to the app, so we offer a clear message and a way out (sign out).
 */
function NoAccessScreen(): React.JSX.Element {
  const { user, signOutUser } = useAuth();
  return (
    <div id="loginGate" data-theme="dark">
      <div className="login-shell">
        <section className="login-hero">
          <LoginLogo />
          <h1>Eskom Procurement Audit Recovery Command Centre</h1>
          <p>Your account is signed in, but it does not yet have access to this workspace.</p>
        </section>

        <section className="login-panel">
          <div className="eyebrow">Eskom PARCC</div>
          <h2>No access</h2>
          <p>
            You do not currently have access{user?.email ? ` for ${user.email}` : ''}. Please
            contact your administrator to request access, then sign in again.
          </p>
          <button type="button" className="btn" onClick={() => void signOutUser()}>
            Sign out
          </button>
        </section>
      </div>
    </div>
  );
}

/**
 * AuthGate — gates the app on authentication AND access. Renders {children} only
 * for an admitted user (`granted`, or `unknown` which fails open so a transient
 * lookup failure never locks the team out); the login gate when signed out; a
 * "no access" screen when authenticated but denied; and a loading state while the
 * verdict resolves.
 */
export function AuthGate({ children }: { children: ReactNode }): React.JSX.Element {
  const { user, access, loading } = useAuth();

  if (loading) {
    return (
      <div id="loginGate" data-theme="dark">
        <div className="empty">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  if (access === 'denied') {
    return <NoAccessScreen />;
  }

  // `granted` and `unknown` (fail-open) reach the app. Admin areas remain guarded
  // by <RequireRole>, so an `unknown` user (role unresolved) only sees the
  // role-shared screens.
  return <>{children}</>;
}

/** Google "G" mark for the sign-in button (inline so there is no asset load). */
function GoogleMark(): React.JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
